import { useMemo, useState } from 'react';
import { useCuttingStore } from './state/useCuttingStore';
import { computeResultMetrics, type ResultMetrics } from './lib/resultMetrics';

/**
 * Block 14 «Раскрой» — вкладка «Результат» (Экран 2.14.2, PR #21, CUT-5.1, EP-5).
 *
 * Пиксельный порт `project.html:431-456` (таблица 16 показателей + кнопка раскрытия)
 * + `styles.css` (`.sc-results-table`, `.highlight`, `.expandable-row`,
 * `.metrics-expand-btn`, портированы в `cutting-theme.css`). Метрики считаются
 * клиентски из `OptimizationResult` (движок, ADR-0079) + деталей проекта
 * (`resultMetrics.ts`). 5 состояний (P-021): idle/loading/empty/error/success.
 *
 * SVG-карта раскроя — CUT-5.2 (следующий PR), здесь ТОЛЬКО таблица метрик.
 */

/** Площадь, м²: 3 знака + «м.кв.» (вербатим прототипа «5.032 м.кв.»). */
const area = (m2: number): string => `${m2.toFixed(3)} м.кв.`;
/** Длина, м: 1 знак + «м» (вербатим «47.2 м»). */
const len = (m: number): string => `${m.toFixed(1)} м`;
/** Процент: 1 знак + «%» (вербатим «91.2 %»). */
const pct = (p: number): string => `${p.toFixed(1)} %`;

/** Метка кромки со скобкой названия, если оно задано (вербатим «Длина кромки 1 (…)»). */
function edgeLabel(n: 1 | 2, name: string): string {
  const base = `Длина кромки ${n}`;
  return name.trim() ? `${base} (${name.trim()})` : base;
}

interface MetricRow {
  label: string;
  value: string;
  /** Подробный показатель (скрыт до раскрытия). */
  expandable?: boolean;
  /** Highlight-строка КИМ. */
  highlight?: boolean;
}

/** 16 строк в порядке прототипа (project.html:435-451) + Проект/Материал (#168-A #3). */
function buildRows(
  m: ResultMetrics,
  edge1Name: string,
  edge2Name: string,
  projectName: string,
  materialText: string,
): MetricRow[] {
  const sheetSize = m.sheetSize ? `${m.sheetSize.length} × ${m.sheetSize.width}` : '—';
  return [
    // ── Проект/Материал (#168-A #3) — перед «Количество листов», всегда видимые ──
    { label: 'Проект', value: projectName.trim() || '—' },
    { label: 'Материал', value: materialText.trim() || '—' },
    // ── Видимые всегда ──
    { label: 'Количество листов', value: String(m.sheetCount) },
    { label: edgeLabel(1, edge1Name), value: len(m.edge1Length) },
    { label: edgeLabel(2, edge2Name), value: len(m.edge2Length) },
    { label: 'Использовано остатков', value: String(m.usedOffcutCount) },
    { label: 'КИМ', value: pct(m.kimPercent), highlight: true },
    // ── Раскрываемые ──
    { label: 'Размер листа', value: sheetSize, expandable: true },
    { label: 'Площадь одного листа', value: area(m.oneSheetArea), expandable: true },
    { label: 'Площадь всех листов', value: area(m.allSheetsArea), expandable: true },
    { label: 'Кол-во используемых остатков', value: String(m.usedOffcutCount2), expandable: true },
    { label: 'Площадь используемых остатков', value: area(m.usedOffcutArea), expandable: true },
    { label: 'Длина резов', value: len(m.cutLengthMeters), expandable: true },
    { label: 'Количество деталей', value: String(m.partCount), expandable: true },
    { label: 'Площадь деталей', value: area(m.partsArea), expandable: true },
    { label: 'Площадь новых остатков', value: area(m.newOffcutArea), expandable: true },
    { label: 'Площадь отходов', value: area(m.wasteArea), expandable: true },
    { label: 'Длина пазов', value: len(m.grooveLength), expandable: true },
  ];
}

export function ResultsTab() {
  const lastResult = useCuttingStore((s) => s.lastResult);
  const cutStatus = useCuttingStore((s) => s.cutStatus);
  const parts = useCuttingStore((s) => s.document.parts);
  const minOffcutSize = useCuttingStore((s) => s.document.settings.minOffcutSize);
  const edge1Name = useCuttingStore((s) => s.document.edges.edge1.name);
  const edge2Name = useCuttingStore((s) => s.document.edges.edge2.name);
  const projectName = useCuttingStore((s) => s.document.meta.name);
  const materialText = useCuttingStore((s) => s.document.meta.materialText);

  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(
    () =>
      lastResult
        ? buildRows(
            computeResultMetrics(lastResult, parts, minOffcutSize),
            edge1Name,
            edge2Name,
            projectName,
            materialText,
          )
        : [],
    [lastResult, parts, minOffcutSize, edge1Name, edge2Name, projectName, materialText],
  );

  // ── Состояния (P-021): loading / error / empty(нет деталей) / idle / success ──
  if (cutStatus === 'loading') {
    return (
      <div className="sc-stub-card" aria-live="polite">
        <h2 className="sc-stub-title">Результат раскроя</h2>
        <p className="sc-stub-text">Раскрой выполняется…</p>
      </div>
    );
  }
  if (cutStatus === 'error') {
    return (
      <div className="sc-stub-card" aria-live="polite">
        <h2 className="sc-stub-title">Результат раскроя</h2>
        <p className="sc-stub-text">Не удалось выполнить раскрой. Попробуйте ещё раз.</p>
      </div>
    );
  }
  if (cutStatus === 'empty') {
    return (
      <div className="sc-stub-card" aria-live="polite">
        <h2 className="sc-stub-title">Результат раскроя</h2>
        <p className="sc-stub-text">Добавьте детали, чтобы выполнить раскрой.</p>
      </div>
    );
  }
  if (!lastResult) {
    // idle — вкладка открыта без запуска раскроя.
    return (
      <div className="sc-stub-card" aria-live="polite">
        <h2 className="sc-stub-title">Результат раскроя</h2>
        <p className="sc-stub-text">Запустите раскрой на вкладке «Детали и кромки».</p>
      </div>
    );
  }

  return (
    <div className="cut-print-metrics">
      <div className="sc-section-title">Результат раскроя</div>

      <table
        className={`sc-results-table${expanded ? ' expanded' : ''}`}
        id="results-metrics-table"
      >
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.label}
              className={
                [r.highlight ? 'highlight' : '', r.expandable ? 'expandable-row' : '']
                  .filter(Boolean)
                  .join(' ') || undefined
              }
            >
              <td>{r.label}</td>
              <td>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        type="button"
        className="metrics-expand-btn"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? '▲ Свернуть подробные показатели' : '▼ Развернуть все показатели'}
      </button>
    </div>
  );
}
